import React, { useEffect } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SettingsUsers } from "@/pages/SettingsUsers";
import { SettingsEntities } from "@/pages/SettingsEntities";
import { SettingsCategories } from "@/pages/SettingsCategories";
import { SettingsDemarcation } from "@/pages/SettingsDemarcation";
import { SettingsMaintenance } from "@/pages/SettingsMaintenance";
import { SettingsModuleAccess } from "@/pages/SettingsModuleAccess";
import { SettingsEmail } from "@/pages/SettingsEmail";
import { SettingsActivity } from "@/pages/SettingsActivity";
import { SettingsTarieven } from "@/pages/SettingsTarieven";
import { SettingsFacturatie } from "@/pages/SettingsFacturatie";
import { SettingsMcMain } from "@/pages/SettingsMcMain";
import { SettingsOorzaken } from "@/pages/SettingsOorzaken";
import { SettingsKeuringtypes } from "@/pages/SettingsKeuringtypes";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, canView } = useAuth();
  const isOwner = hasRole("owner");
  // Onderhoud reads the maintenance router, which lives behind the `kalender`
  // module. With Kalender hidden the tab cannot work, so it is not offered.
  const canMaintenance = canView("kalender");
  const rawTab = location.pathname.includes("entiteiten")
    ? "entiteiten"
    : location.pathname.includes("categorieen")
    ? "categorieen"
    : location.pathname.includes("oorzaken")
    ? "oorzaken"
    : location.pathname.includes("keuringtypes")
    ? "keuringtypes"
    : location.pathname.includes("demarcatie")
    ? "demarcatie"
    : location.pathname.includes("onderhoud")
    ? "onderhoud"
    : location.pathname.includes("meekijken")
    ? "meekijken"
    : location.pathname.includes("email")
    ? "email"
    : location.pathname.includes("activiteit")
    ? "activiteit"
    : location.pathname.includes("mcmain")
    ? "mcmain"
    : location.pathname.includes("tarieven")
    ? "tarieven"
    : location.pathname.includes("facturatie")
    ? "facturatie"
    : "gebruikers";
  // A tab this user may not have, reached by typing the URL, lands on Gebruikers
  // instead of on an empty panel.
  const activeTab =
    (rawTab === "onderhoud" && !canMaintenance) || (["meekijken", "email", "activiteit", "mcmain", "tarieven", "facturatie"].includes(rawTab) && !isOwner)
      ? "gebruikers"
      : rawTab;

  const onTab = (v) => navigate(`/instellingen/${v}`);

  // Keep the address bar honest: the panel that renders is the one the URL says.
  useEffect(() => {
    if (activeTab !== rawTab) navigate(`/instellingen/${activeTab}`, { replace: true });
  }, [activeTab, rawTab, navigate]);

  return (
    <Page console={{ variant: "view", title: "Instellingen",
      facts: [] }}>
      <Tabs value={activeTab} onValueChange={onTab}>
        <TabsList>
          <TabsTrigger value="gebruikers" data-testid="settings-tab-users">Gebruikers</TabsTrigger>
          <TabsTrigger value="entiteiten" data-testid="settings-tab-entities">Entiteiten</TabsTrigger>
          <TabsTrigger value="categorieen" data-testid="settings-tab-categories">Categorieën</TabsTrigger>
          <TabsTrigger value="oorzaken" data-testid="settings-tab-oorzaken">Oorzaken</TabsTrigger>
          <TabsTrigger value="keuringtypes" data-testid="settings-tab-keuringtypes">Keuringtypes</TabsTrigger>
          <TabsTrigger value="demarcatie" data-testid="settings-tab-demarcation">Wie betaalt wat</TabsTrigger>
          {canMaintenance && <TabsTrigger value="onderhoud" data-testid="settings-tab-maintenance">Onderhoud</TabsTrigger>}
          {isOwner && <TabsTrigger value="meekijken" data-testid="settings-tab-meekijken">Meekijken</TabsTrigger>}
          {isOwner && <TabsTrigger value="email" data-testid="settings-tab-email">E-mail</TabsTrigger>}
          {isOwner && <TabsTrigger value="activiteit" data-testid="settings-tab-activity">Activiteit</TabsTrigger>}
          {isOwner && <TabsTrigger value="mcmain" data-testid="settings-tab-mcmain">McMain</TabsTrigger>}
          {isOwner && <TabsTrigger value="tarieven" data-testid="settings-tab-tarieven">Tarieven</TabsTrigger>}
          {isOwner && <TabsTrigger value="facturatie" data-testid="settings-tab-facturatie">Facturatie</TabsTrigger>}
        </TabsList>
        <TabsContent value="gebruikers" className="mt-4">
          <SettingsUsers />
        </TabsContent>
        <TabsContent value="entiteiten" className="mt-4">
          <SettingsEntities />
        </TabsContent>
        <TabsContent value="categorieen" className="mt-4">
          <SettingsCategories />
        </TabsContent>
        <TabsContent value="oorzaken" className="mt-4">
          <SettingsOorzaken />
        </TabsContent>
        <TabsContent value="keuringtypes" className="mt-4">
          <SettingsKeuringtypes />
        </TabsContent>
        <TabsContent value="demarcatie" className="mt-4">
          <SettingsDemarcation />
        </TabsContent>
        {canMaintenance && (
          <TabsContent value="onderhoud" className="mt-4">
            <SettingsMaintenance />
          </TabsContent>
        )}
        {isOwner && (
          <TabsContent value="meekijken" className="mt-4">
            <SettingsModuleAccess />
          </TabsContent>
        )}
        {isOwner && (
          <TabsContent value="email" className="mt-4">
            <SettingsEmail />
          </TabsContent>
        )}
        {isOwner && (
          <TabsContent value="activiteit" className="mt-4">
            <SettingsActivity />
          </TabsContent>
        )}
        {isOwner && (
          <TabsContent value="mcmain" className="mt-4">
            <SettingsMcMain />
          </TabsContent>
        )}
        {isOwner && (
          <TabsContent value="tarieven" className="mt-4">
            <SettingsTarieven />
          </TabsContent>
        )}
        {isOwner && (
          <TabsContent value="facturatie" className="mt-4">
            <SettingsFacturatie />
          </TabsContent>
        )}
      </Tabs>
    </Page>
  );
}
